/**
 * GB人財 コントラスト分岐 レポート文言（構造化データ）
 *
 * `docs/mockups/04_正本_コントラスト分岐_文言テンプレート.md`（正本）を、
 * コードから参照できる構造化データに転記したもの。文言は正本からの忠実な転記であり、
 * 文章の改変・新規執筆は行っていない（正本更新時はこのファイルを差し替える）。
 *
 * 【パターン体系（分類キースキーマ）】
 *  - quadrant（隣接2軸・4象限）: AB堅実 / AD改革 / BC調和 / CD推進
 *  - diagonal（対角2軸）       : AC システム×人 / BD 安定×変化
 *  - single（1軸優勢）         : A / B / C / D
 *  - triple（3軸優勢）         : ABD / ACD / ABC / BCD（残り1軸が低い）
 *  - balanced（全方型）        : ALL（コントラスト判定の対象外）
 *
 * 軸の表示名は gb74Report.ts の AXIS_META と一致:
 *  A=システム / B=安定 / C=人 / D=変化
 *
 * 【コントラスト判定（正本§6）】スプレッド（最高点−最低点）≧6 または 10点突破あり → 鮮明。
 *  スプレッド＜6 → 穏やか。全方型はコントラスト判定の対象外。
 *  ※実際のスコアからの判定（スプレッド計算）は分類処理側（gb74Report.ts, ステップ1）で行う。
 *
 * 【分岐する5要素】タグライン / 思考スタイル / 行動スタイル / 気をつけたいこと / 補完の相手。
 *  正本でパターンによって提供されない要素は undefined（例: 1軸・3軸・対角は思考/行動スタイルの
 *  個別文言を持たない → レポート側で軸別テンプレ等で補完する）。
 */

import type { AxisKey } from './gb74Report';

/** コントラスト水準（鮮明 / 穏やか） */
export type ContrastLevel = 'vivid' | 'gentle';

/** パターンの分類カテゴリ */
export type PatternCategory = 'quadrant' | 'diagonal' | 'single' | 'triple' | 'balanced';

/** レポートの分岐5要素（正本で未提供の要素は欠落） */
export interface ContrastSections {
  /** タグライン */
  tagline?: string;
  /** 思考スタイル */
  thinking?: string;
  /** 行動スタイル */
  acting?: string;
  /** 気をつけたいこと */
  caution?: string;
  /** 補完の相手 */
  complement?: string;
}

/** 1パターンの定義（メタ情報＋コントラスト別文言） */
export interface PatternEntry {
  /** 安定キー（例: 'AB', 'AC', 'A', 'ABD', 'ALL'） */
  key: string;
  /** 表示タイプ名（例: '堅実タイプ'）。balanced/diagonal は通称 */
  typeName: string;
  /** 分類カテゴリ */
  category: PatternCategory;
  /** 構成軸（優勢軸。triple は高い3軸） */
  axes: AxisKey[];
  /** triple のみ: 相対的に低い1軸（盲点） */
  lowAxis?: AxisKey;
  /** コントラスト別の文言。balanced は両キーに同一内容を格納 */
  text: Record<ContrastLevel, ContrastSections>;
}

/**
 * コントラスト判定ルール（正本§6の忠実な実装）。
 * @param spread 4軸スコアのスプレッド（最高点−最低点、各軸0〜12想定）
 * @param hasBreakthrough いずれかの軸が突破ライン（10点）を超えているか
 */
export function contrastFromSpread(spread: number, hasBreakthrough: boolean): ContrastLevel {
  return spread >= 6 || hasBreakthrough ? 'vivid' : 'gentle';
}

// ===== パターン定義データ（正本04の転記） =====

export const CONTRAST_PATTERNS: Record<string, PatternEntry> = {
  // ---- 1. 隣接2軸（4象限） ----
  AB: {
    key: 'AB',
    typeName: '堅実タイプ',
    category: 'quadrant',
    axes: ['A', 'B'],
    text: {
      vivid: {
        tagline: '仕組みと安定の両方を極めた、組織の屋台骨。盲点は「人」と「変化」にあります。',
        thinking:
          '「まず仕組み」で考え、次に「どう守るか」を詰めます。ルール・手順・正確性への関心が際立って強く、曖昧な状態を放置できません。論理的で緻密ですが、人の感情や場の空気を後回しにする傾向があります。',
        acting:
          '計画を立て、手順通りに進め、品質を担保する。この一連の流れを一人で完結できる力があります。ただし、計画が崩れると強いストレスを感じ、対応が硬直化する場面があります。',
        caution:
          '仕組みと安定への志向が極めて強いため、変化が求められる場面で「今のやり方で十分」と判断しやすい傾向があります。周囲が変化を求めているサインを見逃さないよう、月に一度「何か変えるべきことはないか」を自問する習慣が有効です。',
        complement:
          '推進タイプ。 人を巻き込みながら変化を起こす力があり、堅実タイプが作った仕組みに「動き」を加えてくれます。堅実タイプが変化を恐れるとき、推進タイプが「まずやってみよう」と背中を押す関係が理想です。',
      },
      gentle: {
        tagline:
          '仕組みと安定を軸にしつつ、柔軟さも持ち合わせたバランス型。「何が一番得意か」を自覚すると、さらに力を発揮します。',
        thinking:
          '仕組みや手順を大切にしながらも、人の意見や新しいやり方にも耳を傾けられます。突出した強みが見えにくい分、幅広い場面でそつなく対応できる安定感があります。',
        acting:
          '堅実に進めながらも、必要に応じて柔軟に動けます。ただし、どの方向に力を集中すべきか迷う場面が出やすいのも特徴です。「自分の持ち場はここだ」と決めることで、行動にメリハリが生まれます。',
        caution:
          '強みが分散しているため、周囲から「何でもそこそこできるが、決め手がない」と見られる可能性があります。意識的に「仕組みづくり」か「品質の番人」のどちらかに軸足を置くと、チーム内での存在感が明確になります。',
        complement:
          '推進タイプ。 穏やかな堅実タイプの安定感と、推進タイプのスピード感が組み合わさると、堅実だが動きのあるチームが生まれます。推進タイプの勢いに巻き込まれることで、堅実タイプ自身の行動範囲も広がります。',
      },
    },
  },
  AD: {
    key: 'AD',
    typeName: '改革タイプ',
    category: 'quadrant',
    axes: ['A', 'D'],
    text: {
      vivid: {
        tagline:
          '仕組みを壊して作り直す力。組織に最も大きな変化をもたらすタイプですが、人を置き去りにするリスクも最大です。',
        thinking:
          '「今の仕組みは最適か」を常に問い、改善点を見つけると即座に再設計を始めます。論理と構想力が強く、改革の青写真を描く力は際立っています。ただし「人がついてくるか」への関心が薄い傾向があります。',
        acting:
          '構想を形にするスピードが速く、次々と新しい仕組みを打ち出します。しかし、現場が追いつく前に次の変化を始めてしまうことがあり、「朝令暮改」と受け取られるリスクがあります。',
        caution:
          '改革の速度と規模が大きいため、安定寄りや人寄りの社員がついていけなくなることがあります。「仕組みを変える前に、影響を受ける人に10分だけ説明する」というルールを自分に課すと、改革の成功率が大幅に上がります。',
        complement:
          '調和タイプ。 人への配慮と安定維持の力があり、改革タイプが変えた仕組みを「現場に馴染ませる」役割を担います。改革タイプが設計し、調和タイプが浸透させる。この組み合わせが最も摩擦の少ない変革を実現します。',
      },
      gentle: {
        tagline:
          '仕組みと変化の両方に関心があり、改善を着実に進められるタイプ。急進的ではないからこそ、周囲の信頼を得やすい強みがあります。',
        thinking:
          '仕組みの改善に関心がありながら、現実的な範囲で変化を進めようとします。大胆な構想よりも、実行可能な改善を積み重ねる思考が特徴です。',
        acting:
          '変化を起こす力と、仕組みで定着させる力の両方を持っていますが、どちらも中程度のため、大きな変革の旗振り役には向きにくい場面もあります。「小さく変えて、仕組みで定着させる」サイクルが最も力を発揮するスタイルです。',
        caution:
          '改革への関心はあるものの、推進力がやや穏やかなため、周囲に遠慮して変化を先送りしてしまうことがあります。「変えたいこと」を言語化し、チームに共有する習慣が、穏やかな改革力を活かす鍵になります。',
        complement:
          '調和タイプ。 穏やかな改革タイプが「変えたい方向」を示し、調和タイプが「現場の声」を吸い上げて橋渡しする。互いに穏やかなため、衝突が少なく持続的な改善が実現しやすい組み合わせです。',
      },
    },
  },
  BC: {
    key: 'BC',
    typeName: '調和タイプ',
    category: 'quadrant',
    axes: ['B', 'C'],
    text: {
      vivid: {
        tagline:
          '人を支え、安定を守る。組織の心理的安全性を一手に担えるタイプですが、変化に対して受け身になるリスクがあります。',
        thinking:
          '「人がどう感じるか」と「今の状態を壊さないか」の2点から物事を判断します。共感力と安定志向が際立って高く、チームの安心感の源泉になっています。一方で、データや仕組みよりも感覚と関係性で判断する傾向があります。',
        acting:
          '困っている人を見つけるとすぐに動き、チームの雰囲気を一定に保つ力があります。ただし、自分の負荷を周囲に見せないため、気づかないうちに過剰な負担を抱え込むことがあります。',
        caution:
          '人と安定への配慮が極めて強いため、「変えるべきだが、変えると誰かが困る」状況で現状維持に流れやすい傾向があります。「この状態を維持することで、長期的に誰が困るか」を考える視点が、調和タイプの判断力を一段引き上げます。',
        complement:
          '改革タイプ。 仕組みを変える力と構想力があり、調和タイプが「変えられない」と感じている壁を突破する推進力を持っています。調和タイプが守る安全な土台の上で、改革タイプが仕組みを変える。この役割分担が最も機能します。',
      },
      gentle: {
        tagline:
          '人と安定を大切にしながら、他の要素にも目を配れるバランスの良いタイプ。「自分の強みはどこか」を明確にすることで、チーム内の役割がはっきりします。',
        thinking:
          '人への配慮を軸にしつつも、仕組みや変化への関心もある程度持ち合わせています。突出した偏りがないため、多くの場面で違和感なく機能できる安定感があります。',
        acting:
          '周囲の様子を見ながら丁寧に動くスタイルです。急激な変化には慎重ですが、必要性が理解できれば柔軟に対応できます。自分から変化を起こすよりも、変化に対応する力が強い傾向です。',
        caution:
          '強みが分散しているため、チームの中で「この人にはこれを任せたい」という役割が見えにくくなることがあります。「人の相談窓口」や「品質チェックの番人」など、1つの役割を意識的に引き受けると、チーム内の存在感が増します。',
        complement:
          '改革タイプ。 穏やかな調和タイプが安定の土台を守り、改革タイプが変化の方向性を示す。穏やかな調和タイプは改革タイプの意図を現場に翻訳する力があり、「変化の通訳者」として機能します。',
      },
    },
  },
  CD: {
    key: 'CD',
    typeName: '推進タイプ',
    category: 'quadrant',
    axes: ['C', 'D'],
    text: {
      vivid: {
        tagline: '変化を起こす力と、人を巻き込む力。この2つが同時に動く稀有なタイプです。',
        thinking:
          '「まず人」で考え始め、関係性やチームの空気から状況を読み取ります。そこから「何を変えればいいか」の発想が自然に生まれます。仕組みやデータよりも、人の動きと可能性に焦点を当てる傾向があります。',
        acting:
          '周囲を巻き込みながら一気に動くスタイルです。一人で黙々と進めるより、声をかけ合いながら進める方が力を発揮します。スピード感がある一方で、計画や段取りが追いつかないことがあります。',
        caution:
          '人と変化への推進力が際立って強いため、仕組みや安定を軽視しがちです。アイデアを形にするには、途中で「これを仕組みとして定着させるにはどうするか」を一度立ち止まって考える時間が必要です。',
        complement:
          '堅実タイプ。 仕組みと安定で組織を支える力があり、推進タイプが生み出した変化を「維持できる形」に落とし込んでくれます。推進タイプが起こし、堅実タイプが固める。この組み合わせが最も持続的な変革を実現します。',
      },
      gentle: {
        tagline:
          '人を大切にしながら変化にも前向き。どちらにも偏りすぎない柔軟さが強みです。「自分が最も力を発揮できる場面」を知ることで、さらに活きます。',
        thinking:
          '人への関心と変化への関心をバランスよく持っています。急進的ではなく、人の反応を見ながら少しずつ変化を進めるスタイルです。突出した推進力はありませんが、周囲に受け入れられやすい変化の起こし方ができます。',
        acting:
          '人と連携しながら、無理のない範囲で新しいことに取り組めます。スピードよりも丁寧さが特徴で、大きな変革の先頭には立ちにくいものの、変革チームの一員としては非常に機能します。',
        caution:
          '柔軟さがある反面、「何を最も大切にしているか」が周囲から見えにくくなることがあります。「自分は人の力を引き出すことが得意」や「新しいアイデアを試すのが好き」など、自分の軸を言葉にしておくと、チーム内での役割が明確になります。',
        complement:
          '堅実タイプ。 穏やかな推進タイプが「やってみたいこと」を口に出し、堅実タイプが「それを実現する段取り」を組む。互いに穏やかなテンポで動けるため、小さな改善を着実に積み重ねるペアとして長期的に機能します。',
      },
    },
  },

  // ---- 5. 対角型（2軸優勢・対角）。気をつけたいこと・補完は鮮明/穏やか共通 ----
  AC: {
    key: 'AC',
    typeName: 'システム×人 対角型',
    category: 'diagonal',
    axes: ['A', 'C'],
    text: {
      vivid: {
        tagline:
          '仕組みを整える力と、人を大切にする力。本来対立しやすい2つの力が共存する、組織のバランサーです。',
        caution:
          '「仕組みを守りたい自分」と「人を優先したい自分」が場面ごとに入れ替わるため、判断に時間がかかることがあります。どちらの自分が反応しているかを意識するだけで、判断速度が上がります。',
        complement:
          '安定×変化 対角型または変化 1軸。「変化」と「安定」の推進力を外部から補うことで、システム×人 対角型の葛藤が行動に転換されます。',
      },
      gentle: {
        tagline: '仕組みと人の両方に目が届く、安定感のある存在です。',
        caution:
          '「仕組みを守りたい自分」と「人を優先したい自分」が場面ごとに入れ替わるため、判断に時間がかかることがあります。どちらの自分が反応しているかを意識するだけで、判断速度が上がります。',
        complement:
          '安定×変化 対角型または変化 1軸。「変化」と「安定」の推進力を外部から補うことで、システム×人 対角型の葛藤が行動に転換されます。',
      },
    },
  },
  BD: {
    key: 'BD',
    typeName: '安定×変化 対角型',
    category: 'diagonal',
    axes: ['B', 'D'],
    text: {
      vivid: {
        tagline:
          '安定を守る力と、変化を起こす力。相反する2つの力を内に秘めた、状況対応力の高いタイプです。',
        caution:
          '「変えたい自分」と「守りたい自分」の間で板挟みになりやすく、周囲から「どちらをやりたいのか分からない」と見られることがあります。場面ごとに「今はどちらモードか」を宣言すると、チームとの連携がスムーズになります。',
        complement:
          'システム×人 対角型または人 1軸。人への配慮と仕組みの整理を外部から補うことで、安定×変化 対角型の状況対応力が安定して発揮されます。',
      },
      gentle: {
        tagline: '守ることも変えることもでき、場面に応じて切り替えられる柔軟さがあります。',
        caution:
          '「変えたい自分」と「守りたい自分」の間で板挟みになりやすく、周囲から「どちらをやりたいのか分からない」と見られることがあります。場面ごとに「今はどちらモードか」を宣言すると、チームとの連携がスムーズになります。',
        complement:
          'システム×人 対角型または人 1軸。人への配慮と仕組みの整理を外部から補うことで、安定×変化 対角型の状況対応力が安定して発揮されます。',
      },
    },
  },

  // ---- 6. 1軸優勢。タグラインが鮮明/穏やかで分岐、補完は共通 ----
  A: {
    key: 'A',
    typeName: 'システム 1軸',
    category: 'single',
    axes: ['A'],
    text: {
      vivid: {
        tagline:
          '仕組みの設計と改善に誰よりも強い関心。人・変化・安定のどれを補うかで活躍の幅が変わります',
        complement: '人 が高い人',
      },
      gentle: {
        tagline:
          '仕組みへの関心を軸に、幅広く対応できるタイプ。得意分野をもう1つ見つけると一段上に',
        complement: '人 が高い人',
      },
    },
  },
  B: {
    key: 'B',
    typeName: '安定 1軸',
    category: 'single',
    axes: ['B'],
    text: {
      vivid: {
        tagline: '正確さと安定を誰よりも大切にする番人。変化が必要な場面での柔軟性が成長の鍵です',
        complement: '変化 が高い人',
      },
      gentle: {
        tagline:
          '安定志向を軸に、堅実に仕事を進めるタイプ。新しいことへの小さな一歩が世界を広げます',
        complement: '変化 が高い人',
      },
    },
  },
  C: {
    key: 'C',
    typeName: '人 1軸',
    category: 'single',
    axes: ['C'],
    text: {
      vivid: {
        tagline:
          '人への感受性が際立つ、チームの心理的安全の守護者。仕組みやデータへの関心を補うと判断力が増します',
        complement: 'システム が高い人',
      },
      gentle: {
        tagline:
          '人を大切にする姿勢を軸に、チームの潤滑油として機能するタイプ。自分自身のケアも忘れずに',
        complement: 'システム が高い人',
      },
    },
  },
  D: {
    key: 'D',
    typeName: '変化 1軸',
    category: 'single',
    axes: ['D'],
    text: {
      vivid: {
        tagline:
          '変化と挑戦への渇望が突出。組織を動かすエンジンですが、ブレーキ役との連携が不可欠です',
        complement: '安定 が高い人',
      },
      gentle: {
        tagline:
          '変化への前向きさを軸に、柔軟に対応できるタイプ。1つを最後までやり切る力を意識すると信頼が増します',
        complement: '安定 が高い人',
      },
    },
  },

  // ---- 7. 3軸優勢。タグラインが鮮明/穏やかで分岐。lowAxis が盲点 ----
  ABD: {
    key: 'ABD',
    typeName: 'ABD型',
    category: 'triple',
    axes: ['A', 'B', 'D'],
    lowAxis: 'C',
    text: {
      vivid: {
        tagline: '仕組み・安定・変化の3つを回せる稀有なタイプ。唯一の盲点は「人の気持ち」です',
      },
      gentle: {
        tagline: '幅広い力を持つ万能型。人への意識を少し加えるだけで、さらにチームから頼られます',
      },
    },
  },
  ACD: {
    key: 'ACD',
    typeName: 'ACD型',
    category: 'triple',
    axes: ['A', 'C', 'D'],
    lowAxis: 'B',
    text: {
      vivid: {
        tagline:
          '仕組み・人・変化の3方向に動ける行動派。「安定させる」ことへの意識が弱く、築いたものの維持が課題です',
      },
      gentle: {
        tagline: '多方面に関心が広い行動派。1つのやり方を続ける「粘り」を加えると安定感が増します',
      },
    },
  },
  ABC: {
    key: 'ABC',
    typeName: 'ABC型',
    category: 'triple',
    axes: ['A', 'B', 'C'],
    lowAxis: 'D',
    text: {
      vivid: {
        tagline:
          '仕組み・守り・人の3つで組織を支える安定の柱。変化への抵抗感を自覚し、意識的に「変えてみる」機会を作ることが成長の鍵です',
      },
      gentle: {
        tagline: '堅実さと人への配慮を兼ね備えた信頼型。小さな変化への挑戦が、新しい強みを開きます',
      },
    },
  },
  BCD: {
    key: 'BCD',
    typeName: 'BCD型',
    category: 'triple',
    axes: ['B', 'C', 'D'],
    lowAxis: 'A',
    text: {
      vivid: {
        tagline:
          '守り・人・変化の3軸で状況対応力が高い実践派。仕組み化・言語化の意識が弱く、ノウハウが属人化しやすい傾向があります',
      },
      gentle: {
        tagline:
          '現場力と対人力を兼ね備えた実践型。やり方を「見える化」する習慣が、チームへの貢献をさらに広げます',
      },
    },
  },

  // ---- 8. 全方型（バランス型）。コントラスト判定の対象外（両キーに同一内容） ----
  ALL: {
    key: 'ALL',
    typeName: '全方型',
    category: 'balanced',
    axes: ['A', 'B', 'C', 'D'],
    text: {
      vivid: {
        tagline:
          '4つの軸がバランスよく配分された、偏りのないタイプです。どの方向にも動ける柔軟さがある一方、「この人にはこれ」という明確な持ち味が周囲から見えにくいことがあります。',
        thinking:
          '状況に応じて仕組み・人・安定・変化のどの視点からも考えられます。突出した方向性はない代わりに、一方に偏った判断をしにくい特徴があります。',
        acting:
          'チームの中で求められる役割に柔軟に対応できます。ただし、自分から「これがやりたい」と手を挙げることが少なく、受け身に見られることがあります。',
        caution:
          '全方型は「何でもできるが、何をしたいか分からない」と感じやすいタイプです。4軸の中で「少しだけ高い軸」に意識を向け、そこを自分の持ち場として宣言することで、チーム内での立ち位置が明確になります。',
        complement:
          '全方型は特定のタイプとの補完関係を持ちません。むしろ「どのタイプとも組める」こと自体が強みです。偏りの強い鮮明タイプ（どの象限でも）と組むと、全方型の柔軟性が「相手の盲点を埋める緩衝材」として機能します。',
      },
      gentle: {
        tagline:
          '4つの軸がバランスよく配分された、偏りのないタイプです。どの方向にも動ける柔軟さがある一方、「この人にはこれ」という明確な持ち味が周囲から見えにくいことがあります。',
        thinking:
          '状況に応じて仕組み・人・安定・変化のどの視点からも考えられます。突出した方向性はない代わりに、一方に偏った判断をしにくい特徴があります。',
        acting:
          'チームの中で求められる役割に柔軟に対応できます。ただし、自分から「これがやりたい」と手を挙げることが少なく、受け身に見られることがあります。',
        caution:
          '全方型は「何でもできるが、何をしたいか分からない」と感じやすいタイプです。4軸の中で「少しだけ高い軸」に意識を向け、そこを自分の持ち場として宣言することで、チーム内での立ち位置が明確になります。',
        complement:
          '全方型は特定のタイプとの補完関係を持ちません。むしろ「どのタイプとも組める」こと自体が強みです。偏りの強い鮮明タイプ（どの象限でも）と組むと、全方型の柔軟性が「相手の盲点を埋める緩衝材」として機能します。',
      },
    },
  },
};

/** 隣接2軸の軸ペア → パターンキー（順不同で引けるよう両順を登録） */
const QUADRANT_PAIR_TO_KEY: Record<string, string> = {
  AB: 'AB',
  BA: 'AB',
  AD: 'AD',
  DA: 'AD',
  BC: 'BC',
  CB: 'BC',
  CD: 'CD',
  DC: 'CD',
};

/** 対角2軸の軸ペア → パターンキー */
const DIAGONAL_PAIR_TO_KEY: Record<string, string> = {
  AC: 'AC',
  CA: 'AC',
  BD: 'BD',
  DB: 'BD',
};

/** 隣接2軸ペアからパターンキーを引く（該当しなければ対角を試す）。無ければ null */
export function pairToPatternKey(first: AxisKey, second: AxisKey): string | null {
  const pair = `${first}${second}`;
  return QUADRANT_PAIR_TO_KEY[pair] ?? DIAGONAL_PAIR_TO_KEY[pair] ?? null;
}

/** パターン定義を取得 */
export function getPatternEntry(key: string): PatternEntry | null {
  return CONTRAST_PATTERNS[key] ?? null;
}

/** パターンキー＋コントラストから分岐5要素を取得（全方型はコントラスト無視で同一） */
export function getContrastSections(key: string, contrast: ContrastLevel): ContrastSections | null {
  const entry = CONTRAST_PATTERNS[key];
  if (!entry) return null;
  return entry.text[contrast];
}
